import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import { Contact } from "../../../interfaces/DTO/crm/contact";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import {
  BeneficiaryIDCode,
  BeneficiaryIdentificationType,
  BeneficiaryInfo,
} from "../../../interfaces/DTO/mislaka_xml_insurence_models";
import { RelationType } from "../../../interfaces/DTO/mislaka_xml_pension_models";
import { MimshakPensionService } from "../../../services/mimshakPensionService";
import { formatName } from "../../../utils/namesFormater";

interface ContactChildrenRowProps {
  contact: Contact;
  spouse?: Contact;
  /** the family's MimshakData - its beneficiaries are offered as children */
  mimshakData: MimshakData[];
  onSave: (contactId: number, fields: Partial<Contact>) => Promise<void>;
  /** the "+" (add a child) - only while the client card is in edit mode */
  canAdd?: boolean;
}

/** the same child - by id number, otherwise by name + birth date */
const sameChild = (a: BeneficiaryInfo, b: BeneficiaryInfo) => {
  if (a.id_number?.trim() && b.id_number?.trim())
    return a.id_number.trim() === b.id_number.trim();
  return (
    (a.first_name ?? "").trim() === (b.first_name ?? "").trim() &&
    (a.last_name ?? "").trim() === (b.last_name ?? "").trim() &&
    (a.birth_date ?? "").slice(0, 10) === (b.birth_date ?? "").slice(0, 10)
  );
};

const childAge = (birthDate?: string): number | null => {
  if (!birthDate || isNaN(Date.parse(birthDate))) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate()))
    age--;
  return age;
};

const ageText = (birthDate?: string) => {
  const age = childAge(birthDate);
  if (age === null) return "גיל לא ידוע";
  return age < 1 ? "פחות משנה" : `גיל ${age}`;
};

const CHILD_COLOR = "#00bcd4";
const ADULT_CHILD_COLOR = "#9e9e9e";
const childColor = (birthDate?: string) => {
  const age = childAge(birthDate);
  return age !== null && age >= 21 ? ADULT_CHILD_COLOR : CHILD_COLOR;
};

const firstLetter = (child: BeneficiaryInfo) =>
  child.first_name?.trim().charAt(0) || "?";

const toChild = (b: Partial<BeneficiaryInfo>): BeneficiaryInfo => ({
  identification_type: BeneficiaryIdentificationType.INDIVIDUAL,
  id_code: BeneficiaryIDCode.ID_NUMBER,
  first_name: b.first_name?.trim() ?? "",
  last_name: b.last_name?.trim() ?? "",
  id_number: b.id_number?.trim() ?? "",
  birth_date: (b.birth_date ?? "").slice(0, 10),
});

/** a child - small avatar, name and age */
const ChildAvatar: React.FC<{
  child: BeneficiaryInfo;
  size?: number;
  onClick?: () => void;
  title?: string;
}> = ({ child, size = 44, onClick, title }) => (
  <Tooltip title={title ?? ""}>
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: size + 32,
        cursor: onClick ? "pointer" : "default",
        borderRadius: 2,
        py: 0.5,
        "&:hover": onClick ? { backgroundColor: "action.selected" } : undefined,
      }}
    >
      <Avatar
        sx={{
          bgcolor: childColor(child.birth_date),
          width: size,
          height: size,
          fontSize: size * 0.45,
        }}
      >
        {firstLetter(child)}
      </Avatar>
      <Typography
        variant="body2"
        fontWeight={600}
        noWrap
        sx={{ maxWidth: "100%", mt: 0.25 }}
      >
        {child.first_name?.trim() || "ללא שם"}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        {ageText(child.birth_date)}
      </Typography>
    </Box>
  </Tooltip>
);

/** a suggested child in the dialog - like the client's details: avatar, name, age, id */
const SuggestionCard: React.FC<{
  child: BeneficiaryInfo;
  source: string;
  onAdd: () => void;
  disabled: boolean;
  showAvatar?: boolean;
  /** a child already chosen - clicking edits it */
  chosen?: boolean;
}> = ({
  child,
  source,
  onAdd,
  disabled,
  showAvatar = true,
  chosen = false,
}) => (
  <Box
    onClick={disabled ? undefined : onAdd}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 1.5,
      py: 1,
      borderRadius: 2,
      backgroundColor: "action.hover",
      cursor: disabled ? "default" : "pointer",
      border: "1px solid transparent",
      "&:hover": disabled
        ? undefined
        : { borderColor: "primary.main", backgroundColor: "action.selected" },
    }}
  >
    {showAvatar && (
      <Avatar
        sx={{ bgcolor: childColor(child.birth_date), width: 40, height: 40 }}
      >
        {firstLetter(child)}
      </Avatar>
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography fontWeight={600} noWrap>
        {`${child.first_name?.trim() ?? ""} ${child.last_name?.trim() ?? ""}`.trim() ||
          "ללא שם"}
      </Typography>
      <Typography variant="body2" color="text.secondary" noWrap>
        {ageText(child.birth_date)}
        {child.id_number?.trim()
          ? ` · ת.ז ${child.id_number.trim()}`
          : " · ללא ת.ז"}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {source}
      </Typography>
    </Box>
    {chosen ? (
      <EditIcon fontSize="small" color="action" />
    ) : (
      <AddIcon color="primary" />
    )}
  </Box>
);

type DialogMode =
  | { kind: "pick" }
  | { kind: "new" }
  | { kind: "edit"; original: BeneficiaryInfo };

/**
 * ילדים בכרטיס הלקוח: שורת אווטרים קטנים, ו-+ שפותח דיאלוג – ילדים שכבר הוזנו לבן/בת הזוג ומוטבים מהקופות
 * (לחיצה מוסיפה), או ילד חדש מאפס. כך לא מזינים פעמיים פרטי ילדים משותפים.
 */
const ContactChildrenRow: React.FC<ContactChildrenRowProps> = ({
  contact,
  spouse,
  mimshakData,
  onSave,
  canAdd = true,
}) => {
  const children = useMemo(
    () => contact.data?.family_data ?? [],
    [contact.data?.family_data],
  );
  const spouseChildren = useMemo(
    () => spouse?.data?.family_data ?? [],
    [spouse?.data?.family_data],
  );

  const [mode, setMode] = useState<DialogMode | null>(null);
  const [draft, setDraft] = useState<BeneficiaryInfo>(toChild({}));
  const [alsoForSpouse, setAlsoForSpouse] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // the spouse's children that are not this client's yet
  const fromSpouse = useMemo(
    () =>
      spouseChildren.filter(
        (c) => !children.some((mine) => sameChild(mine, c)),
      ),
    [spouseChildren, children],
  );

  // beneficiaries in the family's funds that are not a child yet (nor one of the clients) - children first
  const fromFunds = useMemo(() => {
    const clientIds = [contact.israeli_id, spouse?.israeli_id].filter(Boolean);
    const products = mimshakData.flatMap((md) => md.data?.products || []);
    return MimshakPensionService.getBeneficiaryInfoDistinct(products)
      .filter(
        (b) =>
          !clientIds.includes(b.id_number) &&
          !children.some((c) => sameChild(c, b)) &&
          !fromSpouse.some((c) => sameChild(c, b)),
      )
      .sort(
        (a, b) =>
          Number(b.relation_type === RelationType.CHILD) -
          Number(a.relation_type === RelationType.CHILD),
      );
  }, [
    mimshakData,
    contact.israeli_id,
    spouse?.israeli_id,
    children,
    fromSpouse,
  ]);

  const close = () => {
    if (saving) return;
    setMode(null);
    setConfirmDelete(false);
  };

  const run = async (action: () => Promise<void>, closeAfter = true) => {
    setSaving(true);
    try {
      await action();
      if (closeAfter) close();
    } finally {
      setSaving(false);
    }
  };

  const saveChildren = (target: Contact, next: BeneficiaryInfo[]) =>
    onSave(target.id!, { data: { ...target.data, family_data: next } });

  // a click on a suggestion adds it right away (the dialog stays open for the next one)
  const addSuggestion = (child: BeneficiaryInfo) =>
    run(() => saveChildren(contact, [...children, toChild(child)]), false);

  const openNew = () => {
    setDraft({ ...toChild({}), last_name: contact.last_name ?? "" });
    setAlsoForSpouse(true);
    setMode({ kind: "new" });
  };

  const openEdit = (child: BeneficiaryInfo) => {
    setDraft({ ...child, birth_date: (child.birth_date ?? "").slice(0, 10) });
    setConfirmDelete(false);
    setMode({ kind: "edit", original: child });
  };

  const draftProblem = (() => {
    if (!draft.first_name?.trim()) return "חסר שם פרטי";
    if (draft.id_number && !/^\d{5,9}$/.test(draft.id_number))
      return "מספר זהות לא תקין";
    if (draft.birth_date && new Date(draft.birth_date) > new Date())
      return "תאריך הלידה בעתיד";
    const others =
      mode?.kind === "edit"
        ? children.filter((c) => !sameChild(c, mode.original))
        : children;
    if (others.some((c) => sameChild(c, draft))) return "הילד כבר קיים";
    return null;
  })();

  const saveDraft = () =>
    run(async () => {
      const child = {
        ...draft,
        first_name: draft.first_name?.trim(),
        last_name: draft.last_name?.trim(),
      };
      if (mode?.kind === "edit") {
        const original = mode.original;
        await saveChildren(
          contact,
          children.map((c) =>
            sameChild(c, original) ? { ...c, ...child } : c,
          ),
        );
        // the same child of the spouse - updated too, so it is never entered twice
        if (spouse && spouseChildren.some((c) => sameChild(c, original))) {
          await saveChildren(
            spouse,
            spouseChildren.map((c) =>
              sameChild(c, original) ? { ...c, ...child } : c,
            ),
          );
        }
      } else {
        await saveChildren(contact, [...children, child]);
        if (
          spouse &&
          alsoForSpouse &&
          !spouseChildren.some((c) => sameChild(c, child))
        ) {
          await saveChildren(spouse, [...spouseChildren, child]);
        }
      }
    });

  const deleteChild = () =>
    run(async () => {
      if (mode?.kind !== "edit") return;
      await saveChildren(
        contact,
        children.filter((c) => !sameChild(c, mode.original)),
      );
    });

  const spouseName = spouse ? spouse.first_name || formatName(spouse) : "";
  const isForm = mode?.kind === "new" || mode?.kind === "edit";

  return (
    <>
      {/* the children row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          py: 1,
          borderRadius: 2,
          backgroundColor: "action.hover",
        }}
      >
        <Box sx={{ color: "text.secondary", display: "flex" }}>
          <ChildCareIcon fontSize="small" />
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ width: 84, flexShrink: 0 }}
        >
          ילדים
        </Typography>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          {children.map((child, i) => (
            <ChildAvatar
              key={child.id_number || i}
              child={child}
              onClick={() => openEdit(child)}
              title="לחץ לעריכה"
            />
          ))}
          {canAdd && (
            <Tooltip title="הוסף ילד">
              <IconButton
                color="primary"
                onClick={() => setMode({ kind: "pick" })}
                sx={{
                  border: "2px dashed",
                  borderColor: "primary.light",
                  width: 44,
                  height: 44,
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
          {children.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
              לא הוזנו ילדים
            </Typography>
          )}
        </Box>
      </Box>

      <Dialog open={!!mode} onClose={close} maxWidth="sm" fullWidth dir="rtl">
        <IconButton
          onClick={close}
          disabled={saving}
          size="small"
          sx={{ position: "absolute", left: 12, top: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogTitle sx={{ pl: 6 }}>
          {mode?.kind === "edit"
            ? "עריכת ילד של"
            : mode?.kind === "new"
              ? "ילד חדש ל"
              : "ילדים של"}
          {mode?.kind === "new" ? "" : " "}
          {formatName(contact)}
        </DialogTitle>

        <DialogContent>
          {mode?.kind === "pick" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {children.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    נבחרו ({children.length})
                  </Typography>
                  <Grid container spacing={1}>
                    {children.map((c, i) => (
                      <Grid item xs={12} sm={6} key={c.id_number || i}>
                        <SuggestionCard
                          child={c}
                          source={
                            spouseChildren.some((sc) => sameChild(sc, c))
                              ? "משותף"
                              : `רק של ${contact.first_name || formatName(contact)}`
                          }
                          onAdd={() => openEdit(c)}
                          disabled={saving}
                          chosen
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              {fromSpouse.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    הילדים של {spouseName} (לחץ להוספה)
                  </Typography>
                  <Grid container spacing={1}>
                    {fromSpouse.map((c, i) => (
                      <Grid item xs={12} sm={6} key={c.id_number || i}>
                        <SuggestionCard
                          child={c}
                          source={`הוזן אצל ${spouseName}`}
                          onAdd={() => addSuggestion(c)}
                          disabled={saving}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              {fromFunds.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    מוטבים שנמצאו בקופות (לחץ להוספה)
                  </Typography>
                  <Grid container spacing={1}>
                    {fromFunds.map((c, i) => (
                      <Grid item xs={12} sm={6} key={c.id_number || i}>
                        <SuggestionCard
                          child={c}
                          source="מוטב בקופות"
                          onAdd={() => addSuggestion(c)}
                          disabled={saving}
                          showAvatar={false}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              {fromSpouse.length === 0 && fromFunds.length === 0 && (
                <Typography color="text.secondary">
                  אין ילדים מוצעים – הוסף ילד חדש
                </Typography>
              )}
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={openNew}
                disabled={saving}
                sx={{ alignSelf: "flex-start" }}
              >
                ילד חדש
              </Button>
            </Box>
          )}

          {isForm && (
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}
              >
                <Avatar
                  sx={{
                    bgcolor: childColor(draft.birth_date),
                    width: 56,
                    height: 56,
                    fontSize: "1.5rem",
                  }}
                >
                  {firstLetter(draft)}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {`${draft.first_name ?? ""} ${draft.last_name ?? ""}`.trim() ||
                      "ילד חדש"}
                  </Typography>
                  <Typography color="text.secondary">
                    {ageText(draft.birth_date)}
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="שם פרטי"
                    value={draft.first_name ?? ""}
                    onChange={(e) =>
                      setDraft({ ...draft, first_name: e.target.value })
                    }
                    fullWidth
                    autoFocus
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="שם משפחה"
                    value={draft.last_name ?? ""}
                    onChange={(e) =>
                      setDraft({ ...draft, last_name: e.target.value })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="מספר זהות"
                    value={draft.id_number ?? ""}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        id_number: e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 9),
                      })
                    }
                    inputProps={{ inputMode: "numeric", dir: "ltr" }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="date"
                    label="תאריך לידה"
                    value={draft.birth_date ?? ""}
                    onChange={(e) =>
                      setDraft({ ...draft, birth_date: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
              </Grid>
              {mode?.kind === "new" && spouse && (
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      checked={alsoForSpouse}
                      onChange={(e) => setAlsoForSpouse(e.target.checked)}
                    />
                  }
                  label={`ילד משותף – להוסיף גם ל${spouseName}`}
                />
              )}
              {mode?.kind === "edit" &&
                spouse &&
                spouseChildren.some((c) => sameChild(c, mode.original)) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    ילד משותף – השינויים יעודכנו גם אצל {spouseName}
                  </Typography>
                )}
              {draftProblem && draft.first_name?.trim() && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {draftProblem}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          {mode?.kind === "edit" &&
            (confirmDelete ? (
              <Button
                color="error"
                variant="contained"
                size="large"
                onClick={deleteChild}
                disabled={saving}
              >
                בטוח? הסר מ{contact.first_name || "הלקוח"}
              </Button>
            ) : (
              <Button
                color="error"
                size="large"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setConfirmDelete(true)}
                disabled={saving}
              >
                הסר
              </Button>
            ))}
          <Box sx={{ flex: 1 }} />
          {saving && !isForm && <CircularProgress size={20} />}
          {mode?.kind === "new" ? (
            <Button
              variant="outlined"
              size="large"
              onClick={() => setMode({ kind: "pick" })}
              disabled={saving}
            >
              חזור
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="large"
              onClick={close}
              disabled={saving}
            >
              {mode?.kind === "pick" ? "סיימתי" : "חזור"}
            </Button>
          )}
          {isForm && (
            <Button
              variant="contained"
              size="large"
              onClick={saveDraft}
              disabled={!!draftProblem || saving}
              startIcon={
                saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              שמור
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContactChildrenRow;
